import * as React from "react";

import { useFirstFocusableRef, useDisableTabTabster } from "@1js/acui-focus";

export type TabListFocusControllerProps = {
  /**
   * Children elements within the div
   */
  children?: React.ReactNode;
  /**
   * Custom styles
   */
  className?: string;

  /**
   * Callback ref to control focus on the tablist
   */
  firstFocusableRef?: React.Ref<{ focus: () => void }>;
};

export const TabListFocusController = React.forwardRef(
  (
    props: TabListFocusControllerProps,
    ref: React.ForwardedRef<HTMLDivElement>
  ) => {
    const { className, children, firstFocusableRef } = props;

    const divRef = React.useRef<HTMLDivElement>(null);
    // Hook to ensure we focus the correct tab when we
    // call focus on the tablist container
    useFirstFocusableRef({ firstFocusableRef, focusSelected: true }, divRef);

    // Hook to disable tab so we can manually move tab focus
    const disable = useDisableTabTabster();

    // We need code to allow focus to bubble up so that keytips
    // on the ribbon tabs will dismiss if the user uses arrow keys
    React.useEffect(() => {
      const onMoveFocus = (e: HTMLElementEventMap["tabster:movefocus"]) => {
        e.preventDefault();
        e.detail.next?.focus();
      };

      const div = divRef.current;

      div?.addEventListener("tabster:movefocus", onMoveFocus);

      return div
        ? () => {
            div.removeEventListener("tabster:movefocus", onMoveFocus);
          }
        : undefined;
    }, [divRef]);

    return (
      <div
        role={"toolbar"}
        data-is-focusable={"true"}
        ref={divRef ? divRef : ref}
        className={className}
        tabIndex={0}
        {...disable}
      >
        {children}
      </div>
    );
  }
);
